import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password?email=${encodeURIComponent(email)}`);
      setSent(true);
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">N</span>
              </div>
              <span className="text-2xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
                NYRAV TOURS
              </span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm text-center" data-testid="forgot-password-success">
            <div className="w-16 h-16 bg-[#2A9D8F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#2A9D8F]" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Check Your Email
            </h1>
            <p className="text-[#4A4A4A] mb-6">
              If an account with <strong>{email}</strong> exists, you'll receive a password reset link shortly.
            </p>
            <p className="text-sm text-[#4A4A4A] mb-6">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <Button
              onClick={() => setSent(false)}
              variant="outline"
              className="w-full rounded-full"
            >
              Try Another Email
            </Button>
            <p className="text-center text-sm text-[#4A4A4A] mt-6">
              <Link to="/admin/login" className="text-[#FF6B35] hover:underline">← Back to Login</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">N</span>
            </div>
            <span className="text-2xl font-bold text-[#004E64]" style={{ fontFamily: 'Playfair Display, serif' }}>
              NYRAV TOURS
            </span>
          </Link>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm" data-testid="forgot-password-form">
          <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#FF6B35]" />
          </div>
          <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Forgot Password?
          </h1>
          <p className="text-[#4A4A4A] text-center mb-8">
            No worries! Enter your email address and we'll send you a reset link.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm text-[#4A4A4A] mb-2 block">Email Address</label>
              <Input 
                type="email"
                required
                placeholder="admin@nyravtours.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-xl"
                data-testid="forgot-email"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full h-12"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <p className="text-center text-sm text-[#4A4A4A] mt-6">
            <Link to="/admin/login" className="text-[#FF6B35] hover:underline flex items-center justify-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
